import { Component, input, computed, inject, effect, ChangeDetectionStrategy, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DocumentMetadata } from '../../types/document-metadata';
import { RouterModule, Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { DocumentTemplateService } from '../../services/document-template.service';
import { Field } from '../../types/fields';
import { OcrPrimeStore } from '../../store/ocr-prime.store';
import { isTemplateOutOfSync } from '../../utils/templates.utils';
import { DefaultValueInputComponent } from '../default-value-input/default-value-input.component';
import { FieldCorrection, SaveFieldCorrectionRequest } from '../../types/field-correction';
import { JsonEditorComponent, JsonEditorOptions } from 'ang-jsoneditor';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { ReprocessTemplateDialogComponent, ReprocessTemplateDialogData } from '../reprocess-template-dialog/reprocess-template-dialog.component';
import { OcrErrorComponent } from '../ocr-error/ocr-error.component';
import { OcrAnchorsComponent } from '../ocr-anchors/ocr-anchors.component';
import { OcrFactsComponent } from '../ocr-facts/ocr-facts.component';
import { WebhookDeliveryComponent } from '../webhook-delivery/webhook-delivery.component';

@Component({
  selector: 'app-ocr-result',
  templateUrl: './ocr-result.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CommonModule, FormsModule, RouterModule, MatIconModule, MatTooltipModule, MatSnackBarModule, MatDialogModule, DefaultValueInputComponent, JsonEditorComponent, OcrErrorComponent, OcrAnchorsComponent, OcrFactsComponent, WebhookDeliveryComponent]
})
export class OcrResultComponent {
  metadata = input<DocumentMetadata | null>(null);

  // UI state
  editingFields = signal<Record<string, any>>({});
  correctionComments = signal<Record<string, string>>({});
  saving = signal<boolean>(false);
  saveError = signal<string | null>(null);
  corrections = signal<FieldCorrection[]>([]);
  correctionsLoading = signal<boolean>(false);

  currentMetadata = computed(() => this.metadata());

  private _lastS3Key: string | null = null;
  private documentTemplateService = inject(DocumentTemplateService);
  private router = inject(Router);
  private snackBar = inject(MatSnackBar);
  private dialog = inject(MatDialog);
  readonly store = inject(OcrPrimeStore);
  readonly isInOcrView = this.router.url.includes('/ocr-view');

  /** Resolved template for the current document */
  template = computed(() => {
    const templateId = this.currentMetadata()?.usedTemplateId;
    if (!templateId) return null;
    return this.store.templatesMap()[templateId] ?? null;
  });

  templateName = computed(() => {
    const tpl = this.template();
    return tpl?.name ?? this.currentMetadata()?.usedTemplateId ?? null;
  });

  structureId = computed(() => {
    return this.template()?.fieldsStructureId || null;
  });

  structureName = computed(() => {
    const sid = this.structureId();
    if (!sid) return null;
    const structure = this.store.structuresMap()[sid];
    return structure?.name || null;
  });

  /** Whether the used template is out of sync with its structure */
  isOutOfSync = computed(() => {
    const tpl = this.template();
    if (!tpl) {
      return true;
    }
    const structure = this.store.getStructureById(tpl.fieldsStructureId || '');
    return isTemplateOutOfSync(tpl, structure);
  });

  /** Map of field name → Field definition resolved from the template's structure */
  fieldsMap = computed(() => {
    const sid = this.structureId();
    if (!sid) return {} as Record<string, Field>;
    return this.store.getFieldsMapForStructure(sid);
  });

  constructor() {
    // Load store data
    this.store.loadTemplates();
    this.store.loadFields();
    this.store.loadStructures();

    // Reset editing state and load corrections when the selected document changes
    effect(() => {
      const key = this.metadata()?.s3Key ?? null;
      if (this._lastS3Key !== key) {
        this.cancelEdit();
        this.saveError.set(null);
        this.saving.set(false);
        this._lastS3Key = key;
        this.loadCorrections(key);
      }
    });
  }

  objectKeys(obj: any): string[] {
    return Object.keys(obj || {});
  }

  statusClass = computed(() => {
    const metadata = this.metadata();
    if (!metadata) return '';

    switch (metadata.status) {
      case 'done':
        return 'text-green-600 dark:text-green-400';
      case 'error':
        return 'text-red-600 dark:text-red-400';
      case 'processing':
        return 'text-blue-600 dark:text-blue-400';
      case 'persisted':
        return 'text-yellow-600 dark:text-yellow-400';
      default:
        return '';
    }
  });

  statusText = computed(() => {
    const metadata = this.metadata();
    if (!metadata) return '';

    switch (metadata.status) {
      case 'done':
        return 'Completed';
      case 'error':
        return 'Error';
      case 'processing':
        return 'Processing';
      case 'persisted':
        return 'Persisted';
      default:
        return metadata.status;
    }
  });

  formattedTimestamp = computed(() => {
    const metadata = this.metadata();
    if (!metadata?.timestamp) return '';

    try {
      const date = new Date(metadata.timestamp);
      return date.toLocaleString();
    } catch (e) {
      return metadata.timestamp;
    }
  });

  ocrResultObject = computed(() => {
    const metadata = this.currentMetadata();
    if (!metadata?.ocrResult) return null;

    const res: any = metadata.ocrResult as any;
    if (typeof res === 'string') {
      try {
        return JSON.parse(res);
      } catch {
        return res;
      }
    }
    return res;
  });

  judgeResult = computed(() => this.currentMetadata()?.ocrJudgeResult ?? null);

  /**
   * Anchor key/value map extracted from the document during the auto
   * template-matching step. Returns `null` when a template was pre-selected
   * by the user (in that case nothing is persisted).
   */
  documentAnchors = computed(() => {
    const raw: any = this.currentMetadata()?.documentAnchors;
    if (!raw) return null;
    if (typeof raw === 'string') {
      try { return JSON.parse(raw); } catch { return null; }
    }
    return raw;
  });

  /**
   * Match result computed against the chosen template during auto
   * template-matching. `null` when a template was pre-selected.
   */
  templateMatchResult = computed(() => {
    const raw: any = this.currentMetadata()?.templateMatchResult;
    if (!raw) return null;
    if (typeof raw === 'string') {
      try { return JSON.parse(raw); } catch { return null; }
    }
    return raw;
  });

  /**
   * Anchor-tree traversal trace recorded during auto template-matching.
   * `null` when a template was pre-selected or no trace was persisted.
   */
  anchorTreeTrace = computed(() => {
    const raw: any = this.currentMetadata()?.anchorTreeTrace;
    if (!raw) return null;
    if (typeof raw === 'string') {
      try { return JSON.parse(raw); } catch { return null; }
    }
    return raw;
  });

  readonly processingMetadata = computed(() => this.currentMetadata()?.processingMetadata ?? null);

  /** Null while the document is only known from the list or the subscription, which do not price. */
  readonly runCost = computed(() => this.currentMetadata()?.runCost ?? null);

  // Read-only when OCR judgment passed, document has been manually reviewed, or is still processing
  isReadOnly = computed(() => {
    const meta = this.currentMetadata();
    const verdict = meta?.ocrJudgeResult?.overallVerdict;
    return !!(meta?.reviewed || verdict === 'pass' || meta?.status === 'processing');
  });

  verdictBadgeClass = computed(() => {
    const verdict = this.judgeResult()?.overallVerdict;
    switch (verdict) {
      case 'pass':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-400';
      case 'needs_review':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-400';
      case 'fail':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-400';
      default:
        return '';
    }
  });

  fieldKeys = computed(() => this.objectKeys(this.judgeResult()?.fieldAssessments || {}));

  // Union of fields from OCR results and judgment assessments
  combinedFieldKeys = computed(() => {
    const keys = new Set<string>();
    const res = this.ocrResultObject();
    if (res && typeof res === 'object') {
      for (const k of Object.keys(res as any)) keys.add(k);
    }
    const assessments = this.judgeResult()?.fieldAssessments || {};
    for (const k of Object.keys(assessments)) keys.add(k);
    return Array.from(keys);
  });

  /** Tracks which object/object-list fields are expanded in the read-only view */
  expandedFields = signal<Record<string, boolean>>({});

  toggleFieldExpanded(field: string): void {
    this.expandedFields.update(ef => ({ ...ef, [field]: !ef[field] }));
  }

  isFieldExpanded(field: string): boolean {
    return !!this.expandedFields()[field];
  }

  /** Check whether a field type is object or object-list */
  isObjectType(fieldType: string | null | undefined): boolean {
    return fieldType === 'object' || fieldType === 'object-list';
  }

  /** Resolve child Field definitions for an object/object-list field */
  getChildFields(field: Field | undefined): Field[] {
    if (!field?.fieldIds?.length) return [];
    const allFields = this.store.fields();
    const map = new Map(allFields.map(f => [f.id, f]));
    return field.fieldIds.map(id => map.get(id)).filter((f): f is Field => !!f);
  }

  /** Get the keys of a nested object value */
  getNestedKeys(val: any): string[] {
    if (val && typeof val === 'object' && !Array.isArray(val)) {
      return Object.keys(val);
    }
    return [];
  }

  /** Get array items for object-list values */
  getListItems(val: any): any[] {
    return Array.isArray(val) ? val : [];
  }

  /** JSON editor options for object/object-list editing */
  jsonEditorOptions: JsonEditorOptions = (() => {
    const opts = new JsonEditorOptions();
    opts.mode = 'code';
    opts.modes = ['code', 'tree'];
    opts.mainMenuBar = true;
    opts.navigationBar = false;
    opts.statusBar = true;
    return opts;
  })();

  onJsonEditorChange(field: string, event: any): void {
    this.setEditedValue(field, event);
    // Clear any previous JSON errors for this field
    this.jsonErrors.update(e => { const u = { ...e }; delete u[field]; return u; });
  }

  /** Tracks JSON validation errors per field */
  jsonErrors = signal<Record<string, string>>({});

  getJsonError(field: string): string | null {
    return this.jsonErrors()[field] ?? null;
  }

  /** Validate JSON as the user types in the textarea */
  onJsonInput(field: string, value: string): void {
    this.setEditedValue(field, value);
    if (!value.trim()) {
      this.jsonErrors.update(e => { const u = { ...e }; delete u[field]; return u; });
      return;
    }
    try {
      JSON.parse(value);
      this.jsonErrors.update(e => { const u = { ...e }; delete u[field]; return u; });
    } catch (err: any) {
      this.jsonErrors.update(e => ({ ...e, [field]: err.message ?? 'Invalid JSON' }));
    }
  }

  hasJsonError(field: string): boolean {
    return !!this.jsonErrors()[field];
  }

  toPercent(n?: number | null): string {
    if (typeof n !== 'number' || isNaN(n)) return '';
    return `${Math.round(n * 100)}%`;
  }

  formatValue(val: any): string {
    if (val === null || val === undefined) return '';
    if (typeof val === 'object') {
      try { return JSON.stringify(val); } catch { return String(val); }
    }
    return String(val);
  }

  /** Format a scalar value for display (non-object) */
  formatScalarValue(val: any): string {
    if (val === null || val === undefined) return '—';
    if (typeof val === 'boolean') return val ? 'True' : 'False';
    return String(val);
  }

  async copyToClipboard(value: any): Promise<void> {
    const text = this.formatValue(value);
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      this.snackBar.open('Value copied to clipboard', 'Close', { duration: 2000 });
    } catch (err) {
      console.error('Could not copy text: ', err);
    }
  }

  getAssessment(field: string): any {
    const jr = this.judgeResult();
    const fa = jr?.fieldAssessments as Record<string, any> | undefined;
    return fa ? (fa[field] ?? null) : null;
  }

  isEditing(field: string): boolean {
    return field in this.editingFields();
  }

  getEditedValue(field: string): any {
    return this.editingFields()[field] ?? null;
  }

  setEditedValue(field: string, value: any): void {
    this.editingFields.update(fields => ({ ...fields, [field]: value }));
  }

  hasAnyEditing(): boolean {
    return Object.keys(this.editingFields()).length > 0;
  }

  startEdit(field: string, fieldType: string | null | undefined): void {
    if (this.isReadOnly()) {
      return;
    }
    this.saveError.set(null);
    this.jsonErrors.update(e => { const u = { ...e }; delete u[field]; return u; });
    const current = this.ocrResultObject()?.[field];
    // For object/object-list, store the parsed object for the JSON editor
    if (fieldType === 'object' || fieldType === 'object-list') {
      const value = current != null ? current : (fieldType === 'object-list' ? [] : {});
      this.editingFields.update(fields => ({ ...fields, [field]: value }));
    } else {
      // Pass raw value directly; DefaultValueInputComponent handles type coercion
      this.editingFields.update(fields => ({ ...fields, [field]: current ?? null }));
    }
  }

  getCorrectionComment(field: string): string {
    return this.correctionComments()[field] ?? '';
  }

  setCorrectionComment(field: string, value: string): void {
    this.correctionComments.update(comments => ({ ...comments, [field]: value }));
  }

  cancelEdit(field?: string): void {
    if (field) {
      this.editingFields.update(fields => {
        const updated = { ...fields };
        delete updated[field];
        return updated;
      });
      this.correctionComments.update(comments => {
        const updated = { ...comments };
        delete updated[field];
        return updated;
      });
      this.jsonErrors.update(e => { const u = { ...e }; delete u[field]; return u; });
    } else {
      this.editingFields.set({});
      this.correctionComments.set({});
      this.jsonErrors.set({});
    }
    this.saveError.set(null);
  }

  private parseEditedValue(fieldType: string | null | undefined, raw: any): any {
    switch (fieldType) {
      case 'number': {
        if (raw === '' || raw === null || raw === undefined) return null;
        const n = typeof raw === 'number' ? raw : Number(raw);
        return isNaN(n as number) ? null : n;
      }
      case 'boolean': {
        if (typeof raw === 'boolean') return raw;
        if (typeof raw === 'string') {
          const val = raw.trim().toLowerCase();
          if (val === 'true') return true;
          if (val === 'false') return false;
        }
        return Boolean(raw);
      }
      case 'string-list': {
        if (typeof raw !== 'string') return Array.isArray(raw) ? raw.map((v: any) => String(v)) : [];
        return raw.split(',').map(s => s.trim()).filter(s => s.length > 0);
      }
      case 'number-list': {
        if (typeof raw !== 'string') {
          if (Array.isArray(raw)) {
            return raw
              .map((v: any) => Number(v))
              .filter((v: number) => !isNaN(v));
          }
          return [];
        }
        return raw
          .split(',')
          .map(s => s.trim())
          .filter(s => s.length > 0)
          .map(v => Number(v))
          .filter(v => !isNaN(v));
      }
      case 'object':
      case 'object-list': {
        if (typeof raw === 'string') {
          const trimmed = raw.trim();
          if (!trimmed) return fieldType === 'object-list' ? [] : {};
          return JSON.parse(trimmed);
        }
        return raw;
      }
      default:
        return raw;
    }
  }

  saveEdit(field: string, fieldType: string | null | undefined): void {
    if (this.isReadOnly()) {
      this.saveError.set('Document is read-only.');
      return;
    }
    if (this.hasJsonError(field)) {
      this.saveError.set('Please fix the JSON syntax error before saving.');
      return;
    }
    const meta = this.currentMetadata();
    const base = this.ocrResultObject() ?? {};
    if (!meta?.s3Key) {
      this.saveError.set('Missing document key.');
      return;
    }
    const updated = { ...base } as Record<string, any>;
    try {
      updated[field] = this.parseEditedValue(fieldType, this.getEditedValue(field));
    } catch (e: any) {
      this.saveError.set('Invalid JSON: ' + (e.message ?? 'Parse error'));
      return;
    }

    // Prepare updated judge verdict for the edited field (mark as manually overridden)
    let updatedJudge: DocumentMetadata['ocrJudgeResult'] | undefined = meta?.ocrJudgeResult
      ? JSON.parse(JSON.stringify(meta.ocrJudgeResult))
      : {
          overallVerdict: 'needs_review',
          overallConfidence: 0,
          fieldAssessments: {},
          comments: 'Initialized by manual override'
        } as any;

    // The judge's verdict for this field is unrecoverable once the override below overwrites it, so
    // it travels with the correction record. A previous manual override carries no judge verdict.
    let judgeAssessedCorrect: boolean | undefined;

    if (updatedJudge) {
      // Ensure fieldAssessments exists
      (updatedJudge as any).fieldAssessments = (updatedJudge as any).fieldAssessments || {};
      const previous = (updatedJudge as any).fieldAssessments[field] || null;
      if (previous && !previous.manualOverride && typeof previous.correct === 'boolean') {
        judgeAssessedCorrect = previous.correct;
      }
      (updatedJudge as any).fieldAssessments[field] = {
        correct: true,
        confidence: 1,
        comment: previous?.comment ? `${previous.comment} | Overridden manually` : 'Overridden manually',
        manualOverride: true
      };
    }

    // Capture correction info before saving
    const fromValue = base[field];
    const toValue = updated[field];
    const correctionComment = this.getCorrectionComment(field) || undefined;
    const fm = this.fieldsMap();
    const fieldId = fm[field]?.id || field;
    const templateId = meta.usedTemplateId || '';

    this.saving.set(true);
    this.documentTemplateService.updateOcrResult(meta.s3Key, updated, updatedJudge || undefined).subscribe({
      next: (doc) => {
        this.saving.set(false);
        this.cancelEdit(field);
        this.snackBar.open('Field updated successfully', 'Close', { duration: 3000 });

        // Save field correction record and reload corrections list
        if (templateId) {
          const correction: SaveFieldCorrectionRequest = {
            documentId: meta.s3Key,
            templateId,
            fieldId,
            fieldName: field,
            fromValue,
            toValue,
            comment: correctionComment,
            judgeAssessedCorrect,
          };
          this.documentTemplateService.saveFieldCorrection(correction).subscribe({
            next: () => this.loadCorrections(meta.s3Key),
            error: (err) => console.error('Failed to save field correction:', err)
          });
        }
      },
      error: (err) => {
        this.saving.set(false);
        this.saveError.set(err?.error?.message || 'Failed to save changes');
      }
    });
  }

  /** Map of fieldName → corrections for that field */
  correctionsByField = computed(() => {
    const map: Record<string, FieldCorrection[]> = {};
    for (const c of this.corrections()) {
      (map[c.fieldName] ??= []).push(c);
    }
    return map;
  });

  private loadCorrections(s3Key: string | null): void {
    this.corrections.set([]);
    if (!s3Key) return;
    this.correctionsLoading.set(true);
    this.documentTemplateService.getFieldCorrections(s3Key).subscribe({
      next: (data) => {
        this.corrections.set(data);
        this.correctionsLoading.set(false);
      },
      error: (err) => {
        console.error('Failed to load field corrections:', err);
        this.correctionsLoading.set(false);
      }
    });
  }

  openReprocessDialog(): void {
    const meta = this.currentMetadata();
    if (!meta?.s3Key) {
      this.saveError.set('Missing document key.');
      return;
    }
    const data: ReprocessTemplateDialogData = {
      s3Key: meta.s3Key,
      currentTemplateId: meta.usedTemplateId,
      fileName: meta.originalFileName
    };
    const ref = this.dialog.open(ReprocessTemplateDialogComponent, {
      data,
      width: '480px',
      autoFocus: false
    });
    ref.afterClosed().subscribe((result: { templateId: string } | null) => {
      if (result?.templateId) {
        this.snackBar.open('Reprocessing started…', 'Close', { duration: 3000 });
      }
    });
  }

  markReviewed(): void {
    const meta = this.currentMetadata();
    if (!meta?.s3Key) {
      this.saveError.set('Missing document key.');
      return;
    }
    const currentOcr = this.ocrResultObject() ?? {};
    const currentJudge = meta.ocrJudgeResult || undefined;
    this.saving.set(true);
    this.documentTemplateService.updateOcrResult(meta.s3Key, currentOcr, currentJudge as any, true).subscribe({
      next: () => {
        this.saving.set(false);
        this.snackBar.open('Review completed successfully', 'Close', { duration: 3000 });
      },
      error: (err) => {
        this.saving.set(false);
        this.saveError.set(err?.error?.message || 'Failed to mark as reviewed');
      }
    });
  }
}
